/**
 * Created by Ning Jiang on 5/24/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {
    InitialWinPresentationController
} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/InitialWinPresentationController";
import {Timeline, TimelineLite} from "gsap";
import {FruitsGameConfig} from "../../FruitsGameConfig";
import {FruitsGameModuleConfig} from "../../FruitsGameModuleConfig";
import {FruitsSoundConfig} from "../../FruitsSoundConfig";
import {FruitsGameMode} from "../../gamemode/FruitsGameMode";
import {FruitsReelSymbol} from "../../reelsymbol/FruitsReelSymbol";
import {FruitsStickyRespinOverlayView} from "../../screen/respin/FruitsStickyRespinOverlayView";
import {FruitsParsedGameData} from "../../server/data/FruitsParsedGameData";
import {FruitsHelper} from "../../utils/FruitsHelper";
import {FruitsInitialWinCountUp} from "./FruitsInitialWinCountUp";
import {FruitsInitialWPView} from "./FruitsInitialWPView";
import {BelowStakeWinRestriction} from "../../server/BelowStakeWinRestriction";

export class FruitsInitialWPController extends InitialWinPresentationController {

    private _gameView:FruitsInitialWPView;
    private _winningReelIndexes:number[];
    private _nonWinningReelIndexes:number[];
    private _countUp:FruitsInitialWinCountUp;
    private _isBigWin:boolean;
    public static readonly END_ANIMATION:string = "fruitsInitialWPController_endAnimation";
    private _belowStakeWinRestriction:boolean;

    constructor(indexes:[number, number]) {
        super(indexes);

        this._gameView = new FruitsInitialWPView();
        this._countUp = new FruitsInitialWinCountUp();
        this._countUp.position.set(FruitsGameConfig.instance.GAME_WIDTH * 0.5, FruitsGameConfig.instance.GAME_HEIGHT * 0.5 - 30);
        StageManager.getLayer(FruitsGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._countUp);

        EventHandler.addEventListener(this, FruitsInitialWPController.END_ANIMATION, (event:GameEvent) => {
            this._countUp.getEndingAnimation();
            if(this._nonWinningReelIndexes)
                this.undimNonWinningSymbols();
        });
    }

    protected parseFeatureGameData(data:FruitsParsedGameData):void {
        this._isBigWin = data.isBigWin;
        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue(data.totalWin, data.playedBetValue);
    }

    protected hasWin():boolean {
        if(this._isBigWin) {
            return false;
        }

        if(this._gameData!.mode === FruitsGameMode.STICKY_RESPIN && this._gameData!.nextMode === FruitsGameMode.BONUS_GAME) {
            return this._betWinsData != null && this._betWinsData.length > 0;
        }

        if(this._gameData!.nextMode === FruitsGameMode.STICKY_RESPIN) {
            return false;
        }
        return this._betWinsData != null && this._betWinsData.length > 0;
    }

    protected startWinPresentation():void {
        const timeline:TimelineLite = new TimelineLite();

        for(let i:number = 0; i < FruitsGameConfig.instance.REELS_NUM; i++) {
            let sym:FruitsReelSymbol = <FruitsReelSymbol>Reels.getSymbol(i, 0);
            if(sym) {
                sym.changeState({
                    state : FruitsGameConfig.instance.SYMBOL_STATES.normal,
                    fadeInDuration : 0.15,
                    setStack : false
                })
            }
        }

        this._winningReelIndexes = [];
        this._nonWinningReelIndexes = [];


        this._winningReelIndexes = this.parseWinningReelIndexes();
        this._nonWinningReelIndexes = FruitsInitialWPController.calculateNonWinningSymbols(this._winningReelIndexes);

        this.playAllNoWinSymbols(timeline);

        if(!this._belowStakeWinRestriction) {
            this.addWinAnimationsToTimeline(timeline);

            if(this._config.hasWinCountUp && this.addWinCountUpToTimeline) {
                this.addWinCountUpToTimeline(timeline);
            }
        }

        timeline.add(() => this.finish());

        this._timeline = timeline;
    }

    protected addWinAnimationsToTimeline(timeline:Timeline):void {

        EventHandler.dispatchEvent(new GameEvent(FruitsStickyRespinOverlayView.REMOVE_STICKY_SYMBOLS));

        timeline.add(this.highlightWinSymbols());

        if(this._gameData!.currentWinRatio >= 3 && !this._gameData!.isBigWin) {
            timeline.add(this._gameView.buildUpWinningLines(<IBetLineWinData[]>this._betWinsData));
        }
    }

    private static getWinRatio(currentWinRatio:number):number {

        for(let i:number = 0; i < (GameConfig.instance.WIN_RATIOS.normalWin.length - 1); i++) {

            if(currentWinRatio >= GameConfig.instance.WIN_RATIOS.normalWin[i].ratio &&
                currentWinRatio < GameConfig.instance.WIN_RATIOS.normalWin[i + 1].ratio) {
                return i;
            }
        }
        return 3;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            return;
        }

        const winSize:number = FruitsInitialWPController.getWinRatio(this._gameData!.currentWinRatio);


        if(!this._belowStakeWinRestriction) {
            timeline.add(() => SlotGame.sound.playEffect(FruitsSoundConfig.COUNTUP_ENDS));
        }
        timeline.add([
            this._gameView.showWinAnimation(winSize),
            this._countUp.getCountUpAnimation(0, this._gameData!.singleWin, 0, winSize),
            () => this.updateWinField()
        ]);
    }

    private updateWinField() {
        const totWin:number = this._gameData!.totalWin;
        SlotGame.winFieldController.showWinField(totWin);
    }

    private parseWinningReelIndexes():number[] {
        let winningReelIndexes:number[] = [];
        this._betWinsData!.forEach((betLineData) => {
            for(let i:number = 0; i < (<IBetLineWinData>betLineData).nrOfSymbols; i++) {
                let reelIndex:number = i < 2 ? i * 2 + (<IBetLineWinData>betLineData).betLine[i] - 1 : 3 + (i - 2) * 3 + (<IBetLineWinData>betLineData).betLine[i] + 1;

                if(!FruitsHelper.checkItemExists(winningReelIndexes, reelIndex)) {
                    winningReelIndexes.push(reelIndex);
                }
            }
        });
        winningReelIndexes.sort((a, b) => a - b);

        return winningReelIndexes;
    }

    private highlightWinSymbols():TimelineLite {
        const delay:number = 0.02;

        let tl:TimelineLite = new TimelineLite();


        if(!this._belowStakeWinRestriction) {
            tl.add(() => FruitsGameModuleConfig.soundHandler.playInitialWinSound(this._gameData!.currentWinRatioConfig!));
        }
        this._winningReelIndexes.forEach((lineIndex:number) => {
            let sym:FruitsReelSymbol = <FruitsReelSymbol>Reels.getSymbol(lineIndex, 0);

            if(sym) {
                if(lineIndex < 4) {
                    tl.add(sym.changeState({
                        state : FruitsGameConfig.instance.SYMBOL_STATES.win,
                        fadeInDuration : 0,
                        setStack : false
                    }), delay * Math.floor(lineIndex / 2) * 2)
                } else {
                    tl.add(sym.changeState({
                        state : FruitsGameConfig.instance.SYMBOL_STATES.win,
                        fadeInDuration : 0,
                        setStack : false
                    }), delay * Math.floor((lineIndex - 4) / 3 + 2) * 2)
                }
            }
        });
        return tl;
    }

    private dimNonWinningSymbols():void {
        this._nonWinningReelIndexes.forEach((reelId) => {
            let sym:FruitsReelSymbol = <FruitsReelSymbol>Reels.getSymbol(reelId, 0);
            if(sym) {
                sym.dim(0.2);
            }
        });
    }

    private undimNonWinningSymbols():void {
        this._nonWinningReelIndexes.forEach((reelId) => {
            let sym:FruitsReelSymbol = <FruitsReelSymbol>Reels.getSymbol(reelId, 0);
            if(sym) {
                (<FruitsReelSymbol>sym).undim(0);
            }
        });
    }

    private static calculateNonWinningSymbols(winningReels:number[]):number[] {
        let notWinningIndexes:number[] = [];
        for(let i:number = 0; i < 16; i++) {
            if(winningReels.indexOf(i) < 0) {
                notWinningIndexes.push(i);
            }
        }

        return notWinningIndexes;
    }

    private resetSymbolHighlights():void {
        this._winningReelIndexes.forEach(
            (lineIndex:number) => {
                let sym = Reels.getSymbol(lineIndex, 0);
                if(sym) {
                    sym.changeState({
                        state : FruitsGameConfig.instance.SYMBOL_STATES.normal,
                        fadeInDuration : 0,
                        setStack : false
                    });
                }
            }
        )
    }

    protected playAllNoWinSymbols(timeline:TimelineLite) {
        timeline.add(() => this.dimNonWinningSymbols());
    }

    protected stopWinPresentation(isAborted:boolean):void {
        if(isAborted) {
            const tl:TimelineLite = new TimelineLite();
            if(!this._belowStakeWinRestriction) {
                tl.add(() => SlotGame.sound.playEffect(FruitsSoundConfig.COUNTUP_ENDS))
            }
            tl.add([
                this._countUp.onAbortCountUp(this._gameData!.singleWin),
                this._gameView.showWinAnimation(),
            ]);
            tl.add(() => this._gameView.resetWinAnimation());
        }

        super.stopWinPresentation(isAborted);
        this._gameView.hideLines();
        this._gameView.resetWinAnimation();
        this.resetSymbolHighlights();
        SlotGame.winFieldController.showWinField();
    }
}